import { D, S } from "@mobily/ts-belt";
import { useEffect, useMemo, useState } from "react";
import { Cart } from "../../../domain";
import { firstTextError } from "../../../domain/influencerService";
import { useCart, useGtag } from "../../../hooks";
import { useNotification } from "../../../services/Notifications";
import { capture } from "../../../services/posthogCapture";
import { CART_ADD_FAILED } from "../../../services/posthogEvents.js";
import { getParameter, localStorageUtils } from "../../../util";
import {
  calculateState,
  hasVariants,
  isCustomImageUpload,
} from "../components/ProductPage/productAdapter";

/*
 * Pure: the personalization payload for a cart add.
 *
 * The trimmed text fields and the three customer-image urls are a fixed shape
 * that only reads from state, so they are built here rather than inline —
 * `addToCart` is left with the decisions it actually makes.
 */
const buildPersonalization = (state) => ({
  ...D.map(
    {
      notes: state.notes,
      personalizationTo: state.personalizationTo,
      personalizationAddonCharacterName: "",
      personalizationAddonQuote: "",
      recipientEmail: state.recipientEmail,
    },
    S.trim,
  ),
  customerImageUrl: state.customerImage?.imageUrl ?? "",
  customerOriginalImageUrl: state.customerImage?.originalImageUrl ?? "",
  customerCroppedOriginalImageUrl:
    state.customerImage?.croppedOriginalImageUrl ?? "",
});

/*
 * Pure: selected service instances reduced to what the API accepts. Blank text
 * becomes null so an optional service is stored as "no text" rather than "".
 */
const toServicePayload = (services) =>
  (services ?? []).map(({ influencerServiceId, customerText }) => ({
    influencerServiceId,
    customerText: customerText?.trim() || null,
  }));

export function useAddToCart({ product, state, influencer }) {
  const { refresh } = useCart();
  const gtag = useGtag();
  const notify = useNotification();

  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Clear error when state changes
  useEffect(() => {
    setErrorMessage("");
  }, [state]);

  const { resolved, resolvedProductId, soldOut, cartDisabled } = useMemo(
    () => calculateState({ product, state, isAddingToCart, errorMessage }),
    [errorMessage, isAddingToCart, product, state],
  );

  const cartTotal = useMemo(() => {
    const base = resolved?._totalPrice ?? product?._totalPrice ?? 0;
    const addonSum = Object.values(state.addons).reduce(
      (sum, addon) => sum + (addon.rawPrice ?? 0),
      0,
    );
    const serviceSum = (state.services ?? []).reduce(
      (sum, svc) => sum + (svc.totalPrice ?? 0),
      0,
    );
    return base + addonSum + serviceSum;
  }, [resolved, product, state.addons, state.services]);

  const addToCart = async (evt) => {
    try {
      evt.preventDefault();
      if (isAddingToCart || !influencer) return;
      if (hasVariants(product) && !state.selectedVariant) return;
      if (isCustomImageUpload(resolved) && !state.customerImage) return;

      setIsAddingToCart(true);

      const trackingCode =
        getParameter("stc") || localStorageUtils.getWithExpiry("stc");

      const addonsSet = new Set(Object.values(state.addons).map(D.get("name")));

      const serviceValidationError = firstTextError(state.services);

      if (serviceValidationError) {
        setErrorMessage(serviceValidationError);
        notify({ message: serviceValidationError, duration: 5000 });
        setIsAddingToCart(false);
        return;
      }

      const influencerId = influencer.influencer_id ?? null;

      const [addResult] = await Promise.all([
        Cart.addProduct({
          influencerId,
          productId: resolvedProductId,
          personalization: buildPersonalization(state),
          addons: Array.from(addonsSet),
          services: toServicePayload(state.services),
        }),
        Cart.update({ influencerId, trackingCode }),
      ]);

      const { errors, serviceErrors } = addResult;
      if (Array.isArray(errors) && errors.length) {
        const errorMsg = errors[0].message;
        notify({ message: errorMsg, duration: 5000 });
        setErrorMessage(errorMsg);
        capture(CART_ADD_FAILED, {
          product_id: resolvedProductId,
          error: errorMsg,
        });
        // Without this the button stays disabled until unrelated state changes,
        // leaving the customer unable to retry.
        setIsAddingToCart(false);
        return;
      }

      if (Array.isArray(serviceErrors) && serviceErrors.length) {
        const warningMsg = serviceErrors.map((e) => e.error).join("; ");
        notify({ message: warningMsg, duration: 5000 });
      }

      await refresh();
      setErrorMessage("");
      setIsAddingToCart(false);
      document.dispatchEvent(new CustomEvent("cart:open"));
      gtag("event", "add_to_cart", {
        currency: "USD",
        value: cartTotal / 100,
        items: [{ item_id: resolvedProductId, item_name: product.name }],
      });
    } catch (err) {
      console.error(err);
      notify({ message: err.message, duration: 5000 });
      setErrorMessage(err.message);
      setIsAddingToCart(false);
      capture(CART_ADD_FAILED, {
        product_id: resolvedProductId,
        error: err.message,
      });
    }
  };

  return {
    addToCart,
    isAddingToCart,
    errorMessage,
    resolved,
    resolvedProductId,
    soldOut,
    cartDisabled,
    cartTotal,
  };
}
